import { Alert, Avatar, Card, Col, Descriptions, Row, Skeleton } from 'antd';
import legacyLogo from 'assets/images/legacy.png';
import powerZapLogo from 'assets/images/power-zap.png';
import { LanguageContext } from 'contexts/LanguageContext';
import { SystemConsumer } from 'contexts/legacy/SystemContext';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatNextPaymentDate } from 'utils/subscription/formatNextPaymentDate';
import { formatSubscriptionStatus } from 'utils/subscription/formatSubscriptionStatus';
import { loadPlanType } from 'utils/subscription/loadPlanType';
import { Button } from './styles';

const BILLING_PERIOD_UNIT_ID = {
	month: 'month',
	year: 'year',
	biennial: 'biennial_unit',
	trial: 'year',
};

function SubscriptionView({ setActiveTabKey, system: { loadingSubscription, subscription } }) {
	const { userLocale } = useContext(LanguageContext);

	const { isSubscriptionInactive, isOnHold, isTrial } = formatSubscriptionStatus(
		loadingSubscription,
		subscription,
	);

	const nextPaymentDateFormatted = formatNextPaymentDate({
		loadingSubscription,
		nextPaymentDate: subscription?.next_payment,
		userLocale,
	});

	const currentPlanType = loadPlanType(loadingSubscription, subscription);

	if (isSubscriptionInactive || isTrial) {
		return (
			<>
				<Card
					style={{ margin: 5 }}
					title={
						!loadingSubscription && (
							<Button type="primary" onClick={() => setActiveTabKey('5')}>
								<FormattedMessage id="subscription_now" />
							</Button>
						)
					}
				>
					<Skeleton loading={loadingSubscription}>
						<Alert
							message={
								subscription.days_left > 0 ? (
									<>
										<FormattedMessage id="trial_left" />{' '}
										<FormattedMessage
											id="day"
											values={{
												b: (chunks) => <b>{chunks}</b>,
												day: subscription.days_left,
											}}
										/>
									</>
								) : (
									<FormattedMessage
										id="subscription_not_found"
										values={{
											b: (chunks) => <b>{chunks}</b>,
											br: <br />,
										}}
									/>
								)
							}
							type={subscription.days_left > 0 ? 'warning' : 'error'}
							showIcon
						/>
					</Skeleton>
				</Card>
			</>
		);
	}

	if (isOnHold) {
		return (
			<Card
				style={{ margin: 5 }}
				title={
					!loadingSubscription && (
						<Button type="primary" onClick={() => setActiveTabKey('5')}>
							<FormattedMessage id="my_subscription" />
						</Button>
					)
				}
			>
				<Skeleton loading={loadingSubscription}>
					<Alert
						message={
							<>
								<FormattedMessage
									id="renew_failed"
									values={{
										renewalDate: nextPaymentDateFormatted,
										b: (chunks) => <b>{chunks}</b>,
										br: <br />,
									}}
								/>
								<br />
								<FormattedMessage id="on_hold_message" />
							</>
						}
						type="error"
						showIcon
					/>
				</Skeleton>
			</Card>
		);
	}

	return (
		<Card
			style={{ margin: 5 }}
			title={
				!loadingSubscription && (
					<Row gutter={[0, 16]}>
						<Col>
							<Button type="primary" onClick={() => setActiveTabKey('5')}>
								<FormattedMessage id="my_subscription" />
							</Button>
						</Col>
					</Row>
				)
			}
		>
			<Skeleton loading={loadingSubscription}>
				<Card style={{ backgroundColor: '#e8f9fe' }}>
					<Card.Meta
						avatar={<Avatar src={currentPlanType === 'PRO' ? powerZapLogo : legacyLogo} />}
						title={<FormattedMessage id={currentPlanType.toLowerCase()} />}
						description={
							<Descriptions column={1} size="small">
								<Descriptions.Item label={<FormattedMessage id="total_recurring" />}>
									${subscription.total}/
									<FormattedMessage id={BILLING_PERIOD_UNIT_ID[subscription.billing_period]} />
								</Descriptions.Item>
								<Descriptions.Item label={<FormattedMessage id="renew_in" />}>
									{nextPaymentDateFormatted}
								</Descriptions.Item>
								{subscription.pro_numbers && subscription.pro_numbers > 0 && (
									<Descriptions.Item label={<FormattedMessage id="pro_numbers" />}>
										{subscription.pro_numbers}
									</Descriptions.Item>
								)}
								{subscription.basic_numbers && subscription.basic_numbers > 0 && (
									<Descriptions.Item label={<FormattedMessage id="basic_numbers" />}>
										{subscription.basic_numbers}
									</Descriptions.Item>
								)}
							</Descriptions>
						}
					/>
				</Card>
			</Skeleton>
		</Card>
	);
}

export default SystemConsumer(SubscriptionView);
